import { nanoid } from 'nanoid';
import { usePartySocket } from 'partysocket/react';
import type { FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import ReactMarkdown from 'react-markdown';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router';

import type { ChatMessage, Message } from '../shared';

// Message content renderer that handles markdown formatting
function MessageContent({ content, isAI }: { content: string; isAI: boolean }) {
  return (
    <div
      className={`text-sm font-normal py-2.5 ${
        isAI ? 'text-gray-900 dark:text-white' : 'text-white'
      }`}
    >
      <ReactMarkdown
        components={{
          // Make links clickable and styled
          a: ({ node: _node, ...props }) => (
            <a
              {...props}
              className={`underline hover:no-underline ${
                isAI
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-blue-200 hover:text-white'
              }`}
              target="_blank"
              rel="noopener noreferrer"
            />
          ),
          // Style bold text
          strong: ({ node: _node, ...props }) => (
            <strong {...props} className="font-semibold" />
          ),
          // Style lists
          ul: ({ node: _node, ...props }) => (
            <ul {...props} className="list-disc list-inside ml-2 mt-1" />
          ),
          ol: ({ node: _node, ...props }) => (
            <ol {...props} className="list-decimal list-inside ml-2 mt-1" />
          ),
          li: ({ node: _node, ...props }) => <li {...props} className="mb-1" />,
          // Preserve line breaks
          p: ({ node: _node, ...props }) => <p {...props} className="mb-2 last:mb-0" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// Avatar component for users
function Avatar({ role }: { role: 'user' | 'assistant' }) {
  const isAI = role === 'assistant';

  if (isAI) {
    return (
      <div className="w-8 h-8 rounded-full overflow-hidden">
        <img src="/rita-avatar.png" alt="Rita" className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-gray-300 text-gray-700">
      Me
    </div>
  );
}

// Individual chat message component
function ChatBubble({
  message,
  isOwn,
  isLastUserMessage,
  messageStatus,
}: {
  message: ChatMessage;
  isOwn: boolean;
  isLastUserMessage?: boolean;
  messageStatus?: 'sending' | 'sent' | 'delivered' | 'error';
}) {
  const isAI = message.role === 'assistant';
  const timestamp = new Date(message.timestamp || Date.now()).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (isOwn && !isAI) {
    // Own message - right aligned with blue background
    return (
      <div className="flex items-start gap-2.5 justify-end">
        <div
          className="flex flex-col w-full max-w-[320px] leading-1.5 p-4 border-gray-200 bg-blue-500 rounded-s-xl rounded-ee-xl md:bg-blue-500/95 md:backdrop-blur-sm"
          title={timestamp}
        >
          <MessageContent content={message.content} isAI={false} />
          {isOwn && (
            <span className="text-xs font-normal text-blue-100 mt-1">
              {messageStatus === 'sending'
                ? 'Sending...'
                : messageStatus === 'sent'
                  ? 'Sent'
                  : messageStatus === 'delivered'
                    ? 'Delivered'
                    : messageStatus === 'error'
                      ? 'Failed'
                      : isLastUserMessage
                        ? 'Delivered'
                        : ''}
            </span>
          )}
        </div>
        <Avatar role={message.role} />
      </div>
    );
  }

  // AI or other messages - left aligned with gray background
  return (
    <div className="flex items-start gap-2.5">
      <Avatar role={message.role} />
      <div
        className="flex flex-col w-full max-w-[320px] leading-1.5 p-4 border-gray-200 bg-gray-100 rounded-e-xl rounded-es-xl dark:bg-gray-700 md:bg-white/95 md:backdrop-blur-sm md:dark:bg-gray-800/95"
        title={timestamp}
      >
        {isAI && (
          <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
            <span className="text-xs font-semibold text-purple-600 dark:text-purple-300">
              Rio Rita
            </span>
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
              {timestamp}
            </span>
          </div>
        )}
        <MessageContent content={message.content} isAI={true} />
      </div>
    </div>
  );
}

function App() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'reconnecting'
  >('connecting');
  const [pendingMessages, setPendingMessages] = useState<ChatMessage[]>([]);
  const [messageStatus, setMessageStatus] = useState<
    Record<string, 'sending' | 'sent' | 'delivered' | 'error'>
  >({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const { room } = useParams();

  // Simple name for the user since it's just them and Rio Rita
  const [displayName] = useState('Me');

  // Ensure messages is always an array
  const safeMessages = Array.isArray(messages) ? messages : [];

  const socket = usePartySocket({
    party: 'chat',
    room,
    onOpen: () => {
      setConnectionStatus('connected');

      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }

      // Send room name to server when connection opens
      if (room) {
        socket.send(
          JSON.stringify({
            type: 'setRoom',
            roomName: room,
          })
        );
      }

      // Resend any pending messages and failed messages
      const allMessagesToRetry = [
        ...pendingMessages,
        ...safeMessages.filter(
          (msg) => msg.user === displayName && messageStatus[msg.id] === 'error'
        ),
      ];

      for (const message of allMessagesToRetry) {
        setMessageStatus((prev) => ({ ...prev, [message.id]: 'sending' }));
        socket.send(
          JSON.stringify({
            type: 'add',
            ...message,
          } satisfies Message)
        );
      }

      if (allMessagesToRetry.length > 0) {
        setPendingMessages([]);
      }
    },
    onClose: () => {
      setConnectionStatus('disconnected');

      // Schedule reconnection attempt
      reconnectTimeoutRef.current = setTimeout(() => {
        setConnectionStatus('reconnecting');
      }, 3000);
    },
    onError: () => {
      setConnectionStatus('disconnected');
    },
    onMessage: (evt) => {
      try {
        const message = JSON.parse(evt.data as string) as Message;
        if (message.type === 'add') {
          const foundIndex = safeMessages.findIndex((m) => m.id === message.id);
          if (foundIndex === -1) {
            // probably someone else who added a message
            setMessages((prevMessages) => {
              const currentMessages = Array.isArray(prevMessages) ? prevMessages : [];
              return [
                ...currentMessages,
                {
                  id: message.id,
                  content: message.content,
                  user: message.user,
                  role: message.role,
                  timestamp: message.timestamp,
                },
              ];
            });
          } else {
            // this usually means we ourselves added a message
            // and it was broadcasted back
            // so let's replace the message with the new message
            setMessages((prevMessages) => {
              const currentMessages = Array.isArray(prevMessages) ? prevMessages : [];
              return currentMessages
                .slice(0, foundIndex)
                .concat({
                  id: message.id,
                  content: message.content,
                  user: message.user,
                  role: message.role,
                  timestamp: message.timestamp,
                })
                .concat(currentMessages.slice(foundIndex + 1));
            });
          }
        } else if (message.type === 'update') {
          setMessages((prevMessages) => {
            const currentMessages = Array.isArray(prevMessages) ? prevMessages : [];
            return currentMessages.map((m) =>
              m.id === message.id
                ? {
                    id: message.id,
                    content: message.content,
                    user: message.user,
                    role: message.role,
                  }
                : m
            );
          });
        } else if (message.type === 'all') {
          // Handle messages list
          const messagesList = Array.isArray(message.messages) ? message.messages : [];
          setMessages(messagesList);
        } else if (message.type === 'ack') {
          // Handle message acknowledgments
          setMessageStatus((prev) => ({
            ...prev,
            [message.messageId]:
              message.status === 'received'
                ? 'sent'
                : message.status === 'processed'
                  ? 'delivered'
                  : 'error',
          }));

          // Remove from pending messages if acknowledged
          if (message.status === 'processed') {
            setPendingMessages((prev) =>
              prev.filter((msg) => msg.id !== message.messageId)
            );
          }
        }
      } catch {
        // Silently ignore parse errors
      }
    },
  });

  // Auto-scroll to bottom when new messages arrive
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally scroll when message count changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Message timeout handling - mark messages as failed if no ack received within 30 seconds
  useEffect(() => {
    const timeoutHandlers = new Map<string, ReturnType<typeof setTimeout>>();

    for (const [messageId, status] of Object.entries(messageStatus)) {
      if (status === 'sending') {
        // Set a timeout for this message
        const timeoutId = setTimeout(() => {
          setMessageStatus((prev) => ({ ...prev, [messageId]: 'error' }));
        }, 30000); // 30 second timeout

        timeoutHandlers.set(messageId, timeoutId);
      } else {
        const existingTimeout = timeoutHandlers.get(messageId);
        if (existingTimeout) {
          // Clear timeout if message status changed
          clearTimeout(existingTimeout);
          timeoutHandlers.delete(messageId);
        }
      }
    }

    return () => {
      // Cleanup all timeouts
      for (const timeout of timeoutHandlers.values()) {
        clearTimeout(timeout);
      }
    };
  }, [messageStatus]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const chatMessage: ChatMessage = {
      id: nanoid(8),
      content: inputValue.trim(),
      user: displayName,
      role: 'user',
      timestamp: Date.now(),
    };

    // Add message to local state optimistically
    setMessages((prevMessages) => {
      const currentMessages = Array.isArray(prevMessages) ? prevMessages : [];
      return [...currentMessages, chatMessage];
    });

    // If connected, send immediately; otherwise, queue for later
    if (connectionStatus === 'connected' && socket.readyState === WebSocket.OPEN) {
      setMessageStatus((prev) => ({ ...prev, [chatMessage.id]: 'sending' }));
      socket.send(
        JSON.stringify({
          type: 'add',
          ...chatMessage,
        } satisfies Message)
      );
    } else {
      setPendingMessages((prev) => [...prev, chatMessage]);
      setMessageStatus((prev) => ({ ...prev, [chatMessage.id]: 'sending' }));
    }

    setInputValue('');
  };

  return (
    <div className="flex flex-col h-full w-full relative min-h-screen md:min-h-0">
      {/* Background image for medium and larger screens */}
      <div
        className="hidden md:block fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://i.travelapi.com/lodging/1000000/20000/19900/19837/b756c453_z.jpg')",
        }}
      />

      {/* Chat container with backdrop blur for medium+ screens */}
      <div className="flex flex-col h-full max-w-6xl md:mx-auto md:px-8 lg:px-16 relative z-10 md:bg-white/80 md:dark:bg-gray-900/80 md:backdrop-blur-md md:shadow-xl bg-white dark:bg-gray-800 shadow-lg md:mt-8 md:mb-8 md:rounded-lg md:overflow-hidden pt-safe-top pb-safe-bottom md:pt-0 md:pb-0">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-4 bg-white/90 md:bg-transparent md:dark:bg-transparent dark:bg-gray-800 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Chat with Rio Rita
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  connectionStatus === 'connected'
                    ? 'bg-green-400'
                    : connectionStatus === 'connecting' ||
                        connectionStatus === 'reconnecting'
                      ? 'bg-yellow-400'
                      : 'bg-red-400'
                }`}
              ></div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {connectionStatus === 'connected'
                  ? 'Online'
                  : connectionStatus === 'connecting'
                    ? 'Connecting...'
                    : connectionStatus === 'reconnecting'
                      ? 'Reconnecting...'
                      : 'Offline'}
                {pendingMessages.length > 0 && (
                  <span className="ml-1 text-xs text-orange-500">
                    ({pendingMessages.length} pending)
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 md:bg-transparent">
          {safeMessages.length === 0 ? (
            <div
              className="flex items-center justify-center h-full"
              style={{ height: 'calc(100% - 2rem)' }}
            >
              <div className="text-center">
                <div className="w-24 h-24 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">
                    <img
                      src="https://imagedelivery.net/j444tmn-dIClF7t-Q3FQdw/1f51e6f0-1ab5-4345-b06d-851eeb360700/360x240"
                      alt="Rio Rita Chat Icon"
                    />
                  </span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 md:text-gray-800 md:dark:text-gray-200">
                  Start the conversation
                </h3>
                <p className="text-gray-500 dark:text-gray-400 md:text-gray-600 md:dark:text-gray-300">
                  Send a message to Rio Rita.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {safeMessages.map((message, index) => {
                // Find the last user message index (much simpler)
                const lastUserMessageIndex = safeMessages
                  .slice()
                  .reverse()
                  .findIndex((m) => m.role === 'user');
                const actualLastUserMessageIndex =
                  lastUserMessageIndex === -1
                    ? -1
                    : safeMessages.length - 1 - lastUserMessageIndex;

                return (
                  <ChatBubble
                    key={message.id}
                    message={message}
                    isOwn={message.user === displayName}
                    isLastUserMessage={
                      index === actualLastUserMessageIndex && message.role === 'user'
                    }
                    messageStatus={messageStatus[message.id]}
                  />
                );
              })}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Form */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white/90 md:bg-transparent md:dark:bg-transparent dark:bg-gray-800 flex-shrink-0">
          <form onSubmit={handleSubmit} className="flex items-center space-x-3">
            <div className="flex-1">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 md:bg-white/90 md:backdrop-blur-sm md:dark:bg-gray-800/90"
                placeholder="Type your message..."
                autoComplete="off"
              />
            </div>
            <button
              type="submit"
              disabled={!inputValue.trim()}
              className="px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const rootElement = document.getElementById('root');
if (rootElement) {
  createRoot(rootElement).render(
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Navigate to={`/${nanoid()}`} />} />
        <Route path="/:room" element={<App />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
